import { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import { formatNumber, formatPercent } from '@/lib/format-utils'
import { exportToCsv } from '@/lib/utils'
import { cn } from '@/lib/utils'
import { getPackageName } from '@/lib/package-utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DropdownMediaGroup } from '@/components/DropdownMediaGroup'
import { MyDatePicker } from '@/components/MyDatePicker'
import dayjs from 'dayjs'
import { create } from 'zustand'
import { useSearchParams, useNavigate } from 'react-router-dom'
import DayRoas14Table from './components/dayn-roas-detail-table'
import { useDay0RoasDatePicker } from './stores/dayn-roas-date-store'

const DAY_OPTIONS = [
  { label: 'Day 0', value: '0' },
  { label: 'Day 3', value: '3' },
  { label: 'Day 7', value: '7' },
  { label: 'Day 14', value: '14' },
  { label: 'Day 30', value: '30' },
  { label: 'Day 60', value: '60' },
  { label: 'Day 90', value: '90' },
  { label: 'Day 120', value: '120' },
  { label: 'Day 150', value: '150' },
  { label: 'Day 180', value: '180' },
]

const DayRoas14Page = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Initialize state from URL params if available
  const initialDay = searchParams.get('day') || '0'
  const initialMediaSource = searchParams.get('media_source') || 'total'

  const [mediaSource, setMediaSource] = useState(initialMediaSource)
  const [selectedDay, setSelectedDay] = useState(initialDay)
  const [customDayInput, setCustomDayInput] = useState('')
  const [isDaySelectOpen, setIsDaySelectOpen] = useState(false)

  const startDate = useDay0RoasDatePicker((state) => state.startDate)
  const endDate = useDay0RoasDatePicker((state) => state.endDate)
  const updateStartDate = useDay0RoasDatePicker(
    (state) => state.updateStartDate,
  )
  const updateEndDate = useDay0RoasDatePicker((state) => state.updateEndDate)

  const selectOptions = useMemo(() => {
    const exists = DAY_OPTIONS.some((option) => option.value === selectedDay)
    if (exists) {
      return DAY_OPTIONS
    }
    return [...DAY_OPTIONS, { label: `Day ${selectedDay}`, value: selectedDay }]
  }, [selectedDay])

  const dayValue = useMemo(() => Number(selectedDay), [selectedDay])

  const maxAllowedEndDay = useMemo(
    () => dayjs().subtract(dayValue + 2, 'day'),
    [dayValue],
  )

  const maxAllowedEndDateStr = useMemo(
    () => maxAllowedEndDay.format('YYYYMMDD'),
    [maxAllowedEndDay],
  )

  // Sync date picker store if needed based on maxAllowedEndDay logic
  useEffect(() => {
    if (!startDate || !endDate) return

    const parsedStart = dayjs(startDate, 'YYYYMMDD', true)
    const parsedEnd = dayjs(endDate, 'YYYYMMDD', true)

    if (!parsedStart.isValid() || !parsedEnd.isValid()) return

    if (!parsedEnd.isAfter(maxAllowedEndDay)) return

    const diffDays = parsedEnd.diff(parsedStart, 'day')
    const safeDiff = Number.isFinite(diffDays) ? Math.max(diffDays, 0) : 0
    const adjustedEndStr = maxAllowedEndDay.format('YYYYMMDD')
    const adjustedStartStr = dayjs(maxAllowedEndDay)
      .subtract(safeDiff, 'day')
      .format('YYYYMMDD')

    if (adjustedEndStr === endDate && adjustedStartStr === startDate) return

    updateEndDate(adjustedEndStr)
    updateStartDate(adjustedStartStr)
  }, [endDate, maxAllowedEndDay, startDate, updateEndDate, updateStartDate])

  const {
    data: roasResponse,
    isLoading,
    error,
  } = useSWR([
    `/troas/dayn/range`,
    { startDate, endDate, mediaSource, day_n: dayValue },
  ])
  const roasData = roasResponse?.data || []

  // Filter data for PID 14 and JP
  const detailData = useMemo(() => {
    if (!roasData) return []
    return roasData.filter((d) => d.country === 'JP' && d.pid === 14)
  }, [roasData])

  const handleDaySelectChange = (value) => {
    setSelectedDay(value)
  }

  const handleCustomDayInputChange = (event) => {
    const value = event.target.value
    if (value === '') {
      setCustomDayInput('')
      return
    }
    const numericValue = Number(value)
    if (Number.isNaN(numericValue) || numericValue < 0 || numericValue > 300) {
      return
    }
    setCustomDayInput(value)
  }

  const handleCustomDayApply = () => {
    if (customDayInput === '') return
    const numericValue = Number(customDayInput)
    if (Number.isNaN(numericValue) || numericValue < 0 || numericValue > 300) {
      return
    }
    setSelectedDay(String(Math.floor(numericValue)))
    setCustomDayInput('')
    setIsDaySelectOpen(false)
  }

  // Quick date range functions
  const setQuickRange = (days) => {
    const endDay = maxAllowedEndDay
    const start = endDay.subtract(days, 'day')
    updateEndDate(endDay.format('YYYYMMDD'))
    updateStartDate(start.format('YYYYMMDD'))
  }

  // Check if current date range matches a quick range
  const isQuickRangeActive = (days) => {
    const endDay = maxAllowedEndDay
    const start = endDay.subtract(days, 'day')
    const expectedStartDate = start.format('YYYYMMDD')
    const expectedEndDate = maxAllowedEndDateStr

    return startDate === expectedStartDate && endDate === expectedEndDate
  }

  const handleExportCsv = () => {
    if (!detailData.length) return

    const filename = `day${dayValue}_roas_14_jp_${dayjs().format(
      'YYYYMMDDHHmmss',
    )}.csv`

    // Get unique package names and countries (though country is fixed to JP)
    const uniquePackageNames = [
      ...new Set(detailData.map((d) => d.packageName).filter(Boolean)),
    ].sort()
    const uniqueCountries = [
      ...new Set(detailData.map((d) => d.country).filter(Boolean)),
    ].sort()

    // Construct headers: Package Name, JP
    const headers = [
      'Package Name',
      ...uniqueCountries.map((c) => c.toUpperCase()),
    ]

    const rows = uniquePackageNames.map((packageCode) => {
      const packageName = getPackageName(packageCode)
      const row = { 'Package Name': packageName }
      uniqueCountries.forEach((country) => {
        const items = detailData.filter(
          (d) => d.packageName === packageCode && d.country === country,
        )
        if (items.length) {
          // Should aggregate if multiple items, though usually one per package+country
          const totalRevenue = items.reduce(
            (sum, d) => sum + d.total_revenue,
            0,
          )
          const totalCost = items.reduce((sum, d) => sum + d.total_cost, 0)
          const roas = totalCost > 0 ? totalRevenue / totalCost : 0
          row[country.toUpperCase()] =
            typeof roas === 'number' ? roas.toFixed(4) : ''
        } else {
          row[country.toUpperCase()] = ''
        }
      })
      return row
    })

    exportToCsv(rows, filename, headers)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            Back
          </Button>
          <h2 className="text-xl font-bold">PID 14 - JP Detail</h2>
        </div>
      </div>

      {/* Controls - Same as Day0RoasPage */}
      <div className="flex items-center gap-4 flex-wrap sm:gap-2 sm:py-2 xs:py-1 xs:gap-1">
        <MyDatePicker
          datePickerStore={useDay0RoasDatePicker}
          getMaxDate="roas-complete"
          showMaxDate={true}
          defaultAllowIncomplete={false}
        />
        <DropdownMediaGroup value={mediaSource} update={setMediaSource} />
        <Select
          value={selectedDay}
          onValueChange={handleDaySelectChange}
          open={isDaySelectOpen}
          onOpenChange={setIsDaySelectOpen}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Day 0" />
          </SelectTrigger>
          <SelectContent>
            <div className="border-b border-muted p-3 space-y-2">
              <div className="text-xs text-muted-foreground">
                Custom Day (0-300)
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={300}
                  value={customDayInput}
                  placeholder="e.g. 45"
                  onChange={handleCustomDayInputChange}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      handleCustomDayApply()
                    }
                  }}
                  className="h-8"
                />
                <Button size="sm" onClick={handleCustomDayApply}>
                  Apply
                </Button>
              </div>
            </div>
            {selectOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button
            variant={isQuickRangeActive(6) ? 'default' : 'outline'}
            size="sm"
            onClick={() => setQuickRange(6)}
          >
            Past 7 Days
          </Button>
          <Button
            variant={isQuickRangeActive(13) ? 'default' : 'outline'}
            size="sm"
            onClick={() => setQuickRange(13)}
          >
            Past 14 Days
          </Button>
          <Button
            variant={isQuickRangeActive(29) ? 'default' : 'outline'}
            size="sm"
            onClick={() => setQuickRange(29)}
          >
            Past 30 Days
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportCsv}>
          Export
        </Button>
      </div>

      {/* Data Table */}
      <div className="relative min-h-[256px]">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center text-lg font-medium">
            Loading day{dayValue} ROAS data...
          </div>
        ) : error ? (
          <div className="flex h-64 items-center justify-center text-lg font-medium text-red-600">
            Failed to load day{dayValue} ROAS data: {error?.message}
          </div>
        ) : (
          <DayRoas14Table data={detailData} dayValue={dayValue} />
        )}
      </div>
    </div>
  )
}

export default DayRoas14Page
